import { ChangeEvent, useCallback, useMemo } from 'react';
import { CheckboxBlockField, NumberInput } from 'components';
import {
  Offer,
  QuoteInformation,
  SessionFormat,
  StudySubject,
  StudyType,
  usePriceTiers,
} from '@app/api';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { hasProduct } from 'lib/billing';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  buttonList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.marginBase * 2,

    [theme.media.desktop]: {
      flexDirection: 'row',
      flexWrap: 'no-wrap',
    },
  },

  shortcuts: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
  },
}));

interface Props {
  showQuantityPriceTier?: boolean;
}

const shortcuts = {
  [StudyType.MODERATED]: [5, 8, 10, 12],
  [StudyType.UNMODERATED]: [8, 10, 20, 50],
  [StudyType.DIARY]: [8, 10, 20, 50],
};

export const NumberOfParticipantsSection = ({
  showQuantityPriceTier = true,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { data: priceTiers } = usePriceTiers();

  const { values, setFieldValue } =
    useFormikContext<Partial<QuoteInformation>>();

  const numberOfParticipants = values.numberOfParticipantRequired || 0;
  const studyType = values.studyType;
  const onPriceTierChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const id = e.currentTarget.value;
      const tier = priceTiers?.find((p) => p.id === id);

      if (tier) {
        if (!!tier.maximum && numberOfParticipants > tier.maximum) {
          setFieldValue('numberOfParticipantRequired', tier.maximum);
        } else if (!!tier.minimum && numberOfParticipants < tier.minimum) {
          setFieldValue('numberOfParticipantRequired', tier.minimum);
        } else if (!tier.minimum && !numberOfParticipants) {
          setFieldValue('numberOfParticipantRequired', 5);
        }
      }
      return true;
    },
    [priceTiers, numberOfParticipants, setFieldValue],
  );

  const onNumberOfParticipantChange = useCallback(
    (numberOfParticipants: number) => {
      if (!showQuantityPriceTier) {
        return;
      }
      const priceTier = priceTiers?.find((p) => {
        if (p.minimum > numberOfParticipants) {
          return false;
        }
        if (!p.maximum) {
          return true;
        }
        return numberOfParticipants <= p.maximum;
      });

      setFieldValue('priceThierId', priceTier?.id);
    },
    [priceTiers, showQuantityPriceTier, setFieldValue],
  );
  const max = useMemo(() => {
    if (values.studySubject === StudySubject.B2B) {
      return 10;
    }
    return undefined;
  }, [values.studySubject]);

  return (
    <div className={classes.container}>
      {showQuantityPriceTier && (
        <div className={classes.buttonList}>
          {priceTiers?.map((priceTier) => (
            <CheckboxBlockField
              label={
                priceTier.maximum
                  ? `${priceTier.minimum || 1} - ${priceTier.maximum}`
                  : `${priceTier.minimum}+`
              }
              onChange={onPriceTierChange}
              type="radio"
              variant="green"
              name="priceThierId"
              value={priceTier.id}
              icon="ri-user-3-line"
              trackingName="priceTier"
            />
          ))}
        </div>
      )}
      <NumberInput
        name="numberOfParticipantRequired"
        id="numberOfParticipants"
        onValueChange={onNumberOfParticipantChange}
        noMargin
        noError
        trackingName="Number of Participants"
        max={max}
      />
      {!showQuantityPriceTier && (
        <div className={classes.shortcuts}>
          <div className={classes.buttonList}>
            {shortcuts[studyType!].map(
              (shortcut) =>
                (!max || shortcut <= max) && (
                  <CheckboxBlockField
                    label={String(shortcut)}
                    onChange={onPriceTierChange}
                    type="radio"
                    variant="green"
                    name="numberOfParticipantRequired"
                    value={shortcut}
                    icon="ri-user-3-line"
                    trackingName="priceTier"
                  />
                ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
